import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { captureFeatureFlagsFromUrl } from "@/lib/feature-flags/bootstrap";
import { installChunkReloadHandler } from "@/lib/chunkReload";
import { queryClient } from "@/lib/queryClient";
import { isPrerendering } from "@/lib/prerender";
import {
  initDecalPreference,
  initDecalAutoEnable,
} from "@/components/portal/charts/decalPreference";
import { routeTree } from "./routeTree.gen";
import "@/lib/feedbackContext"; // Install console.error interceptor for feedback widget
import "./index.css";

// Capture any ff.* URL params into localStorage BEFORE TanStack
// Router mounts. Most routes use validateSearch which whitelists
// known params, so anything else (including our feature-flag
// overrides) is stripped from window.location.search the moment
// the router resolves the route. Capturing here at bootstrap is the
// only reliable way to preserve URL-based flag overrides.
captureFeatureFlagsFromUrl();

// Recover from stale lazy-chunk requests after a deploy by reloading once
// to fetch the fresh index.html. See chunkReload.ts for the full rationale.
installChunkReloadHandler();

// Restore the chart pattern-fills preference before any chart mounts, so the
// first paint already reflects it rather than flashing plain colour first.
initDecalPreference();

// Auto-enable decals under forced-colors/high-contrast — an OS/browser
// accessibility signal, applied regardless of the stored default but never
// overriding an explicit user choice (decalPreference.ts, D2).
initDecalAutoEnable();

// Create router instance
const router = createRouter({ routeTree });

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          {/* Global toast duration 10s (Theme 9b). Sonner's default 4s
              is too short to read multi-line error toasts like the
              line-validation `Fill in required line fields before
              saving: Petty Cash Requisition Lines row 1 - Supplier
              Name; ...` produced by handleSubmit's pre-submit checks.
              Bumping globally keeps every toast call site uniform
              without per-call duration overrides. */}
          <Toaster position="top-right" richColors duration={10000} />
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);

// Signal to the prerenderer (vite.config.ts, renderAfterDocumentEvent) that
// the page is genuinely ready to snapshot. Only runs under the prerenderer -
// it injects window.__PRERENDER_INJECTED before any app code (see the
// `inject` renderer option) - so real visitors never pay for this.
//
// Several simpler signals were tried and each lost to a real race
// (2026-08-02/03):
// - a fixed renderAfterTime let the snapshot beat react-helmet-async's flush,
//   shipping the generic shell <title> on 9 of 13 pages and dropping the
//   noindex meta from /archive/home in production;
// - router.subscribe("onRendered") never fires for the initial mount, only
//   for navigations, so every route timed out;
// - router.load() + a fixed number of animation frames lost to Helmet's own
//   rAF-deferred commit on slower routes;
// - "title differs from the shell" hung forever on /archive/home, whose title
//   legitimately IS the shell title - index.html's default was copied from
//   the very page that route now archives.
//
// The check is a ROUTE-level Helmet tag: every prerendered route sets its own
// description meta, and Helmet stamps what it manages with data-rh. Nothing
// above the route emits a description - this branch removed the root layout's
// Helmet entirely - so a description carrying data-rh proves THIS route's head
// has been written, not merely that React mounted. If a root-level description
// is ever added, this check silently stops proving anything. Held for
// three consecutive frames plus a non-empty title, because Helmet transitions
// (an error-boundary unmount, a redirect stub handing over to its target)
// strip and re-apply head tags, and a snapshot inside that window shipped a
// bare head. If a route never qualifies, the renderer's 60s timeout fails the
// build loudly, and the checks in src/lib/prerenderVerify.ts reject a stale
// snapshot at build time regardless.
if (isPrerendering()) {
  const loaded = router.load().catch((error) => {
    // Surfaced through the renderer's consoleHandler (see vite.config.ts), so a
    // loader failure names itself instead of appearing only as the 60s
    // readiness timeout with no cause attached.
    console.error("[prerender] router.load failed:", error);
  });
  void loaded.then(() => {
    let stableFrames = 0;
    const waitForHelmetFlush = () => {
      if (
        document.head.querySelector('meta[name="description"][data-rh]') &&
        document.title.trim() !== ""
      ) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }
      if (stableFrames >= 3) {
        document.dispatchEvent(new Event("prerender-ready"));
      } else {
        requestAnimationFrame(waitForHelmetFlush);
      }
    };
    requestAnimationFrame(waitForHelmetFlush);
  });
}
